import { LitElement, html } from 'lit';
import { keyed } from 'lit/directives/keyed.js';
import { customElement, property } from 'lit/decorators.js';
import './common/swd-a.js';
import './swd-nav.js';
import './swd-footer.js';
import router from '../router.js';

@customElement('swd-app')
class SwdApp extends LitElement {

  @property({ type: Object, attribute: false })
  route;

  @property({ attribute: false })
  loadError;

  @property({ type: Boolean })
  routeLoading = true;

  @property()
  scrollToHashOnUpdate = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('route-ready', this.handleRouteReady);
    window.addEventListener('hashchange', this.handleHashChange);
    this.bootstrapRoute();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('route-ready', this.handleRouteReady);
    window.removeEventListener('hashchange', this.handleHashChange);
  }

  async bootstrapRoute() {
    if (this.route) {
      this.routeLoading = false;
      return;
    }

    try {
      await router.load();

      // Fallback if route-ready fired before this listener was attached.
      if (!this.route && !this.loadError) {
        this.applyRoute(router.getRoute());
      }
    } catch (error) {
      console.error('Route bootstrap failed', error);
      this.loadError = error;
      this.routeLoading = false;
    }
  }

  updated(changed) {
    if (changed.has('route')) {
      this.route?.load().then(() => { this.scrollToHashOnUpdate = true; });
    }

    if (changed.has('scrollToHashOnUpdate') && this.scrollToHashOnUpdate) {
      this.scrollToCurrentHash('auto');
      this.scrollToHashOnUpdate = false;
    }
  }

  createRenderRoot() {
    return this;
  }

  render() {
    return html`
      <swd-nav></swd-nav>
      <div>
        ${this.renderRouteBody()}
        <swd-footer></swd-footer>
      </div>
    `;
  }

  renderRouteBody() {
    if (this.loadError) {
      return html`
        <section class="route-status">
          <h2>Page could not be loaded</h2>
          <p>
            Something went wrong while loading this page. Please try again.
          </p>
          <button class="btn btn-primary" @click=${this.handleRetryLoad}>
            Try again
          </button>
        </section>
      `;
    }

    if (this.routeLoading || !this.route) {
      return html`
        <section class="route-status" aria-busy="true">
          <p>Loading...</p>
        </section>
      `;
    }

    return keyed(this.route.name, this.route.render(this.route.params));
  }

  handleRouteReady = ({ detail: { route, error } }) => {
    if (error) {
      this.loadError = error;
      this.routeLoading = false;
      return;
    }

    if (this.route?.name === route.name) {
      this.routeLoading = false;
      return;
    }

    this.applyRoute(route);
  };

  applyRoute(route) {
    if (this.route?.name === route.name && !this.loadError) {
      this.routeLoading = false;
      return;
    }

    this.loadError = null;
    this.routeLoading = false;
    this.route = route;

    document.title = `${route.title ? `${route.title} | ` : ''} PowerBax - Standby Power Systems`;
  }

  handleRetryLoad = async () => {
    const route = router.getRoute();

    this.loadError = null;
    this.routeLoading = true;
    this.route = undefined;
    router.lastRouteReadied = undefined;
    route.loaded = false;

    if (route.name && router.routes[route.name]) {
      router.routes[route.name].loaded = false;
    }

    await router.loadCurrent();
  };

  handleHashChange = () => {
    this.scrollToCurrentHash();
  };

  scrollToCurrentHash(behavior = 'smooth') {
    const { hash } = location;
    const view = this.querySelector(':nth-child(2)');

    if (!view) return;

    if (hash === '' || hash === '#') {
      view.scrollTo({ top: 0, left: 0, behavior });
      return;
    }

    view.querySelector(hash)
      ?.scrollIntoView({ behavior });
  }
}